import React from "react";
import { Group, Table, Text, Tooltip } from "@mantine/core";
import {
  IconArrowDown,
  IconArrowUp,
  IconInfoCircle,
} from "@tabler/icons-react";
import type { SortDirection, SortField } from "../types";

interface ReferralTableHeaderProps {
  sortField: SortField;
  sortDirection: SortDirection;
  onSort: (field: SortField) => void;
}

/**
 * Component for displaying table header with sortable columns
 */
export const ReferralTableHeader: React.FC<ReferralTableHeaderProps> = ({
  sortField,
  sortDirection,
  onSort,
}) => {
  const renderSortIcon = (field: SortField) => {
    if (sortField !== field) return null;
    return sortDirection === "asc" ? (
      <IconArrowUp size={14} />
    ) : (
      <IconArrowDown size={14} />
    );
  };

  return (
    <Table.Thead>
      <Table.Tr style={{ backgroundColor: "#f8f9fa" }}>
        <Table.Th style={{ fontWeight: 600, padding: "12px", width: "40px" }}>
          {/* Expand column */}
        </Table.Th>
        <Table.Th
          style={{
            fontWeight: 600,
            padding: "12px",
            cursor: "pointer",
            width: "200px",
            maxWidth: "200px",
          }}
          onClick={() => onSort("email")}
        >
          <Group gap={4}>
            <Text size="sm" fw={600}>
              Referee
            </Text>
            <Tooltip
              label="The name of the person who signed up using your referral link"
              multiline
              w={220}
            >
              <IconInfoCircle
                size={12}
                style={{ color: "var(--mantine-color-dimmed)" }}
              />
            </Tooltip>
            {renderSortIcon("email")}
          </Group>
        </Table.Th>
        <Table.Th
          style={{
            fontWeight: 600,
            padding: "12px",
            width: "200px",
            maxWidth: "200px",
          }}
        >
          <Group gap={4}>
            <Text size="sm" fw={600}>
              Email
            </Text>
            <Tooltip
              label="The email address of the person who signed up"
              multiline
              w={220}
            >
              <IconInfoCircle
                size={12}
                style={{ color: "var(--mantine-color-dimmed)" }}
              />
            </Tooltip>
          </Group>
        </Table.Th>
        <Table.Th
          style={{
            fontWeight: 600,
            padding: "12px",
            cursor: "pointer",
          }}
          onClick={() => onSort("signupSource")}
        >
          <Group gap={4}>
            <Text size="sm" fw={600}>
              Signup Type
            </Text>
            <Tooltip
              label="How the person signed up: Lead (just registered), Full Signup (paid in full), Installments (paying in installments), or Pay by Prep Fees (promotional payment - no referral rewards)"
              multiline
              w={280}
            >
              <IconInfoCircle
                size={12}
                style={{ color: "var(--mantine-color-dimmed)" }}
              />
            </Tooltip>
            {renderSortIcon("signupSource")}
          </Group>
        </Table.Th>
        <Table.Th
          style={{
            fontWeight: 600,
            padding: "12px",
            cursor: "pointer",
          }}
          onClick={() => onSort("paymentStatus")}
        >
          <Group gap={4}>
            <Text size="sm" fw={600}>
              Payout Status
            </Text>
            <Tooltip
              label="The payout status of the referral: Not Started, Partial Payment (installments in progress), or Fully Paid"
              multiline
              w={220}
            >
              <IconInfoCircle
                size={12}
                style={{ color: "var(--mantine-color-dimmed)" }}
              />
            </Tooltip>
            {renderSortIcon("paymentStatus")}
          </Group>
        </Table.Th>
        <Table.Th
          style={{
            fontWeight: 600,
            padding: "12px",
          }}
        >
          <Group gap={4}>
            <Text size="sm" fw={600}>
              Reward Amount
            </Text>
            <Tooltip
              label="The total reward amount for this referral"
              multiline
              w={220}
            >
              <IconInfoCircle
                size={12}
                style={{ color: "var(--mantine-color-dimmed)" }}
              />
            </Tooltip>
          </Group>
        </Table.Th>
        <Table.Th
          style={{
            fontWeight: 600,
            padding: "12px",
            cursor: "pointer",
          }}
          onClick={() => onSort("date")}
        >
          <Group gap={4}>
            <Text size="sm" fw={600}>
              Date Created
            </Text>
            <Tooltip
              label="The date when this person first signed up using your referral link"
              multiline
              w={220}
            >
              <IconInfoCircle
                size={12}
                style={{ color: "var(--mantine-color-dimmed)" }}
              />
            </Tooltip>
            {renderSortIcon("date")}
          </Group>
        </Table.Th>
      </Table.Tr>
    </Table.Thead>
  );
};
